import { useEffect, useState } from "react";
import { CustomDataGrid } from "../../components/common/CustomDataGrid"
import axios from "axios";
import baseURL from "../../configs/api";
import { GridColDef } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import { useTheme } from "../../components/hooks/useTheme";
import useGeneral from "../../components/hooks/useGeneral";
import moment from "moment-timezone";
import { relocationStatusColorCode } from "../../utils/statusColorCode";
import Cookies from "js-cookie";
import useTenant from "../../components/hooks/useTenant";

type Props = {
  date: string;
  branchId: number;
  branchTimeZone: string;
};

export const Relocation = ({ date, branchId, branchTimeZone }: Props) => {
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const { handleTenantError }: any = useTenant();
  const { dateFormat, timeZoneName }: any = useGeneral();
  const { themeStyles } = useTheme();
  const [data, setData] = useState<any[]>([]);
  const [filterData, setFilterData] = useState<any[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  const columns: GridColDef[] = [
    {
      field: "relocationDate",
      headerName: "Relocation Date",
      headerClassName: themeStyles.table.headerBg,
      flex: 2,
      align: "left",
      editable: false,
      sortable: false
    },
    {
      field: "vehicleName",
      headerName: "Vehicle",
      headerClassName: themeStyles.table.headerBg,
      flex: 2,
      align: "left",
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return (
          <Link to="/fleet/vehicle/editvehicles#relocations" state={params?.row?.vehicleId}
            style={{
              color: "var(--app-link-color)",
              textDecoration: "underline",
            }}
          >
            {params?.value}
          </Link>
        )
      }
    },
    {
      field: "fromLocationName",
      headerName: "From Location",
      headerClassName: themeStyles.table.headerBg,
      flex: 1,
      align: "left",
      editable: false,
      sortable: false,
    },
    {
      field: "toLocationName",
      headerName: "To Location",
      headerClassName: themeStyles.table.headerBg,
      flex: 1,
      align: "left",
      editable: false,
      sortable: false,
    },
    {
      field: "reason",
      headerName: "Reason",
      headerClassName: themeStyles.table.headerBg,
      align: "left",
      flex: 1,
      editable: false,
      sortable: false,
    },
    {
      field: "createdAt",
      headerName: "Created At",
      headerClassName: themeStyles.table.headerBg,
      align: "left",
      flex: 1,
      editable: false,
      sortable: false,
    },
    {
      field: "createdBy",
      headerName: "Created By",
      headerClassName: themeStyles.table.headerBg,
      align: "left",
      flex: 1,
      editable: false,
      sortable: false,
    },
    {
      field: "status",
      headerName: "Status",
      headerClassName: themeStyles.table.headerBg,
      flex: 1,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        let color = {
          colorBg: "#000000",
          colorText: "#000000",
          label: ""
        };

        color = relocationStatusColorCode?.find((color: any) => color?.value === params?.value?.trim()?.toLowerCase()) || {
          colorBg: "#000000",
          colorText: "#000000",
          label: ""
        };

        return (
          <div className="flex items-end h-full">
            <span className={`inline-flex text-xs px-[10px] py-[2px] rounded-xl font-semibold ${color.colorBg} ${color.colorText}`}>
              {color?.label}
            </span>
          </div>
        );
      }
    }
  ];

  useEffect(() => {
    if (date) {
      setLoading(true);

      if (branchId > 0) {
        const updatedDate = moment.tz(date, branchTimeZone).endOf('day');
        let formatDateString = updatedDate.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

        axios
          .get(`${baseURL}dailyManifest/GetRelocationsByBranchId/${formatDateString},${branchId}`, {
            headers: {
              'X-Tenant-ID': tenantId,
              "Content-Type": "application/json"
            },
          })
          .then((response) => {
            const result = response?.data?.map((item: any) => {
              return {
                ...item,
                relocationDate: moment(item?.relocationDate).format(dateFormat + " hh:mm A"),
                createdAt: moment(item?.createdAt).format(dateFormat + " hh:mm A")
              }
            })
            setData(result);
            setFilterData(result);
            setLoading(false);
            handleTenantError(null);
          })
          .catch((error) => {
            handleTenantError(error);
          });
      } else {
        const updatedDate = moment.tz(date, timeZoneName).endOf('day');
        let formatDateString = updatedDate.format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');

        axios
          .get(`${baseURL}dailyManifest/GetRelocations/${formatDateString}`, {
            headers: {
              'X-Tenant-ID': tenantId,
              "Content-Type": "application/json"
            },
          })
          .then((response) => {
            const result = response?.data?.map((item: any) => {
              return {
                ...item,
                relocationDate: moment(item?.relocationDate).format(dateFormat + " hh:mm A"),
                createdAt: moment(item?.createdAt).format(dateFormat + " hh:mm A")
              }
            })
            setData(result);
            setFilterData(result);
            setLoading(false);
            handleTenantError(null);
          })
          .catch((error) => {
            handleTenantError(error);
          });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, timeZoneName, branchId, branchTimeZone]);

  useEffect(() => {
    setFilterData(data?.filter((_: any) =>
      _?.vehicleName?.toLowerCase()?.includes(searchValue?.toLowerCase()) ||
      _?.fromLocationName?.toLowerCase()?.includes(searchValue?.toLowerCase()) ||
      _?.toLocationName?.toLowerCase()?.includes(searchValue?.toLowerCase())
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue])

  return (
    <div className={`w-full border ${themeStyles.main.borderColor} rounded-[16px]`}>
      <div className={`flex justify-between items-center p-4 border-b ${themeStyles.main.borderColor}`}>

        <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Relocation</h2>

        <div className={`w-[30%] flex px-[5px] py-[6px] items-center gap-2.5 rounded-lg border ${themeStyles.main.borderColor} shadow-sm`}>

          <span className={`material-symbols-outlined text-[25px] font-light ${themeStyles.main.textSubHeadingColor}`}>
            search
          </span>
          <input
            type="text"
            placeholder="Vehicle, From Location or To Location"
            className={`border-none outline-none w-full ${themeStyles.main.textSubHeadingColor} bg-transparent text-sm`}
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>
      </div>

      <CustomDataGrid
        filteredRows={filterData}
        columns={columns}
        loading={loading}
      />
    </div>
  )
}